import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiFetch, apiThrow } from '../utils/api';
import { BTN_XS } from '../constants/buttons';
import { ROLE_LABELS } from '../constants/statuses';
import { formatDate } from '../utils/dateFormat';
import { useLatestRequest } from '../hooks/useSearchRequest';
import { useToastOrNoop } from '../contexts/ToastContext';
import { friendlyErrorMessage } from './ErrorMessage';
import ConfirmDialog from './ConfirmDialog';

/**
 * THE MANAGER'S SIDE OF "JOIN A SECOND DEPARTMENT" (2026-09-23, P6) — the
 * department's PENDING INVITES, rendered inside `PeopleManager` (Settings →
 * Users). 🔻 The INVITE BY EMAIL form that lived here is DELETED (2026-09-24,
 * owner ruling): it is the "Add & send invite" action of the ONE
 * `AddPersonForm.js`, which carries the same route, key and role rule.
 *
 * 🔑 THE LIST IS ITS OWN STATE, NEVER MERGED INTO `people`. An invite id is a
 * `department_invites` id; used where a user id is expected it would edit the
 * wrong person (plan §4.5). Nothing here reads or writes a user.
 *
 * RULINGS IT CARRIES (owner, 2026-09-23):
 *  - R21 — no Decline anywhere; the manager's control is WITHDRAW.
 *  - R23.6 — an invite waiting on a DEACTIVATED account (`visible_since`
 *    NULL) reads "Pending — account deactivated"; every other reads "Pending
 *    invite". Derived from `visible_since` ALONE — never a date sum in JS.
 *  - R24 — only managers see the list. The PARENT decides `canList`
 *    (site admin, or `canAssignDepartmentRoles` in the active department).
 *
 * 🔁 DEPARTMENT SWITCH: the list is keyed on `departmentId`; a switch clears
 * it at once and `useLatestRequest` drops a late answer for the department the
 * viewer just left, so a stale department's invites never show.
 *
 * 🔻 NO `useUnsavedChangesGuard`: Settings (AdminSettings) is declared OUT of
 * the leave guard by coordinator ruling (`useUnsavedChangesGuard.sites.test.js`),
 * and the form holds an address and a name — cheap to retype.
 *
 * Server contract: `server/routes/departmentInvites.js` (`GET /department`,
 * `POST /:id/withdraw`).
 */
export const PENDING_INVITE_LABEL = 'Pending invite';
export const PENDING_DEACTIVATED_LABEL = 'Pending — account deactivated';

/** R23.6 — the label, from `visible_since` alone. */
export function pendingInviteLabel(invite) {
  return invite && invite.visible_since != null ? PENDING_INVITE_LABEL : PENDING_DEACTIVATED_LABEL;
}

const BADGE_PENDING = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
const BADGE_DEACTIVATED = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';

const roleLabel = (role) => ROLE_LABELS[role] || role;

export default function DepartmentInvitesPanel({
  departmentId,
  canList,
  // Bumped by the parent after "Add & send invite" so the new row appears.
  refreshKey = 0,
}) {
  const toast = useToastOrNoop();
  const validDept = Number.isInteger(departmentId) && departmentId > 0;
  const shouldFetch = validDept && canList;

  const [invites, setInvites] = useState([]);
  const [loadError, setLoadError] = useState('');
  const beginRequest = useLatestRequest();

  const fetchInvites = useCallback(async () => {
    const isCurrent = beginRequest();
    if (!shouldFetch) return;
    try {
      const res = await apiGet(`/api/department-invites/department?department_id=${departmentId}`);
      if (!res.ok) await apiThrow(res);
      const data = await res.json();
      if (!isCurrent()) return;
      setInvites(Array.isArray(data.invites) ? data.invites : []);
      setLoadError('');
    } catch (err) {
      if (!isCurrent()) return;
      setInvites([]);
      // A 403 means "not yours to see" — HIDE the section, never an error
      // (coordinator, 2026-09-23: the list moves onto canAssignDepartmentRoles).
      setLoadError(err && err.status === 403 ? '' : friendlyErrorMessage(err));
    }
  }, [departmentId, shouldFetch, beginRequest]);

  // Clear FIRST, then fetch: the previous department's rows must never sit
  // under the new department's name while the request is in flight.
  useEffect(() => {
    setInvites([]);
    setLoadError('');
    fetchInvites();
  }, [fetchInvites]);

  // The parent bumps `refreshKey` after an "Add & send invite" so the new row
  // shows WITHOUT the clear-first flash above (the department did not change).
  useEffect(() => {
    if (refreshKey > 0) fetchInvites();
  }, [refreshKey, fetchInvites]);

  // ── Withdraw (R21) ─────────────────────────────────────────────────────
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const handleWithdraw = async () => {
    const invite = withdrawTarget;
    if (!invite) return;
    setWithdrawing(true);
    try {
      const res = await apiFetch(`/api/department-invites/${invite.id}/withdraw`, { method: 'POST' });
      if (res.ok) {
        toast.success(`Invite to ${invite.email} withdrawn`);
      } else if (res.status === 404 || res.status === 410) {
        // Already accepted, expired or withdrawn elsewhere: say so, then
        // refresh so the row that is no longer pending disappears.
        let body = {};
        try { body = await res.json(); } catch { /* tolerate non-JSON */ }
        toast.error(body.error || 'That invitation is no longer pending');
      } else {
        await apiThrow(res);
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setWithdrawing(false);
      setWithdrawTarget(null);
      fetchInvites();
    }
  };

  if (!validDept) return null;

  const showList = canList && invites.length > 0;

  return (
    <div data-testid="department-invites-panel">
      {canList && loadError && (
        <p className="mb-3 text-sm text-red-700 dark:text-red-300" data-testid="pending-invites-error">
          Pending invites could not be loaded: {loadError}
        </p>
      )}

      {showList && (
        <section className="mb-4" aria-labelledby="pending-invites-heading" data-testid="pending-invites">
          {/* A deliberate SUB-header of the page's "Users" section (fix round 1,
              2026-09-23): one step down from its `text-lg` title, with a
              divider, so it reads as a section rather than a caption. An <h4>
              because the Users title is the <h3> above it. */}
          <h4
            id="pending-invites-heading"
            className="text-base font-semibold text-gray-700 dark:text-gray-200 pb-2 mb-3 border-b border-gray-200 dark:border-gray-700"
          >
            Pending invites ({invites.length})
          </h4>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
            {invites.map((inv) => {
              const label = pendingInviteLabel(inv);
              return (
                <li
                  key={inv.id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm"
                  data-testid={`pending-invite-${inv.id}`}
                >
                  {/* Full width below `sm` so the badge and Withdraw wrap to their
                      own line instead of squeezing the address mid-word
                      (measured at 400px, 2026-09-23). */}
                  <div className="min-w-0 w-full sm:w-auto sm:flex-1">
                    <div className="font-medium text-gray-800 dark:text-gray-200 break-all">
                      {inv.email}
                      {inv.invited_name ? (
                        <span className="font-normal text-gray-500 dark:text-gray-400"> · {inv.invited_name}</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {roleLabel(inv.role)}
                      {inv.invited_by_name ? ` · invited by ${inv.invited_by_name}` : ''}
                      {` · sent ${formatDate(inv.created_at)}`}
                      {inv.expires_at ? ` · expires ${formatDate(inv.expires_at)}` : ''}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${inv.visible_since != null ? BADGE_PENDING : BADGE_DEACTIVATED}`}
                    data-testid={`pending-invite-label-${inv.id}`}
                  >
                    {label}
                  </span>
                  {/* Shown with the row: the list and withdraw share one gate
                      (the invite right), so whoever sees a row may withdraw it. */}
                  <button
                    type="button"
                    onClick={() => setWithdrawTarget(inv)}
                    className={`${BTN_XS['danger-soft']} min-h-[44px] md:min-h-0`}
                    data-testid={`pending-invite-withdraw-${inv.id}`}
                  >
                    Withdraw
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <ConfirmDialog
        open={!!withdrawTarget}
        title="Withdraw invite"
        message={withdrawTarget ? `Withdraw invite to ${withdrawTarget.email}?` : ''}
        confirmLabel="Withdraw"
        cancelLabel="Keep invite"
        variant="danger"
        busy={withdrawing}
        onConfirm={handleWithdraw}
        onCancel={() => setWithdrawTarget(null)}
      />
    </div>
  );
}
